"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronDown, BookOpen, GraduationCap, BarChart3, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface FaqItem {
  question: string;
  answer: string;
  category: "about" | "eligibility" | "predictor";
}

const FAQS: FaqItem[] = [
  {
    question: "What is CCMT?",
    answer: "CCMT stands for Centralized Counseling for M.Tech./M.Arch./M.Plan. It is a common platform for admission to post-graduate programs in NITs, IIITs, IIEST Shibpur, and several GFTIs (Government Funded Technical Institutes) based on candidates' GATE scores.",
    category: "about"
  },
  {
    question: "What does CCMT mean?",
    answer: "CCMT stands for Centralized Counseling for M.Tech./M.Arch./M.Plan. It represents the centralized, single-window admission process for post-graduate engineering and planning programs across participating premier technical institutions in India.",
    category: "about"
  },
  {
    question: "What is CCMT counselling?",
    answer: "CCMT counselling is the online centralized process through which qualified GATE candidates register, fill their institute and program choices, and get allotted seats in participating NITs, IIITs, and GFTIs based on their GATE scores, category, and preferences.",
    category: "about"
  },
  {
    question: "What is the minimum CGPA for CCMT?",
    answer: "For CCMT counseling, the minimum qualifying degree requirements are typically: 6.5 CGPA (on a 10-point scale) or 60% marks for General/General-EWS/OBC candidates, and 6.0 CGPA (on a 10-point scale) or 55% marks for SC/ST/PwD candidates.",
    category: "eligibility"
  },
  {
    question: "How many rounds are there in CCMT?",
    answer: "CCMT counseling typically consists of three regular rounds (Round 1, Round 2, and Round 3). If seats remain vacant after the regular rounds, they are followed by two Special Rounds and occasionally a National Spot Round (NSR).",
    category: "eligibility"
  },
  {
    question: "What is a Special Round in CCMT?",
    answer: "A Special Round is conducted after the regular rounds of CCMT counseling to fill any remaining vacant seats in NITs, IIITs, and GFTIs. Candidates need to register separately for Special Rounds, and the cutoffs are often lower than regular rounds.",
    category: "eligibility"
  },
  {
    question: "What is NSR / National Spot Round in CCMT?",
    answer: "The National Spot Round (NSR) is the final spot round of seat allotment conducted at the end of CCMT counseling to fill any remaining vacant seats across all participating institutes. It allows new registrations and candidates who haven't secured a seat in previous rounds to participate.",
    category: "eligibility"
  },
  {
    question: "What is NIRF ranking?",
    answer: "NIRF stands for National Institutional Ranking Framework. It is a methodology adopted by the Ministry of Education, Government of India, to rank institutions of higher education in India based on parameters like Teaching, Learning & Resources, Research and Professional Practice, Graduation Outcomes, Outreach and Inclusivity, and Perception.",
    category: "eligibility"
  },
  {
    question: "How does the CCMT College Predictor work?",
    answer: "The CCMT College Predictor works by comparing your GATE score, category, and paper code with 6 years of historical cutoff data (2021-2026). It uses high-fidelity analysis to calculate your probability of admission to various NITs, IIITs, and GFTIs.",
    category: "predictor"
  },
  {
    question: "Is the GATE score college predictor free to use?",
    answer: "Yes, our CCMT College Predictor is completely free to use. You can search, explore, and predict college chances for all branches without any registration fees or hidden costs.",
    category: "predictor"
  },
  {
    question: "What categories are supported by the CCMT College Predictor?",
    answer: "The predictor supports all major categories participating in CCMT counseling: General (OPEN), OBC-NCL, SC, ST, EWS, and their respective PwD (Persons with Disabilities) sub-categories.",
    category: "predictor"
  },
  {
    question: "How accurate is the CCMT cutoff predictor?",
    answer: "Our predictor utilizes official historical seat allocation data from 2021 to 2026. While it provides high-fidelity projections, actual cutoffs for 2026 depend on exam difficulty, candidate preferences, and seat matrix revisions. We advise using predictions as a guiding baseline.",
    category: "predictor"
  },
  {
    question: "When is the CCMT 2026 registration date?",
    answer: "The CCMT 2026 registration date and counseling schedule are announced officially by the organizing institute. Please check the portal regularly or bookmark our page to get direct access to the predictor as soon as registration commences.",
    category: "about"
  },
  {
    question: "Where can I find the CCMT 2026 Round 1 cutoff and seat allotment results?",
    answer: "The CCMT 2026 Round 1 seat allotment results and official Round 1 cutoffs are published on the official CCMT portal. You can explore these cutoffs and check your Round 1 eligibility instantly using our updated Cutoff Explorer and College Predictor.",
    category: "predictor"
  },
  {
    question: "What is the difference between opening rank and closing rank in CCMT?",
    answer: "In CCMT, the 'opening rank' (or opening GATE score) is the highest score at which a candidate was admitted to a specific program/college in a counseling round. The 'closing rank' (or closing GATE score) is the lowest score at which a seat was allocated, serving as the official cutoff score for that program.",
    category: "eligibility"
  },
  {
    question: "What do max score and min score signify in the CCMT cutoff explorer?",
    answer: "In our cutoff explorer tools, 'Max Score' refers to the opening GATE score (the top-ranked candidate's score admitted to that course), and 'Min Score' refers to the closing GATE score (the minimum required score/cutoff to secure a seat).",
    category: "predictor"
  },
  {
    question: "Can I get admission if my GATE score is below the previous year's min score?",
    answer: "Yes, it is possible. Previous years' min scores (closing ranks) are valuable guiding indicators, but cutoffs fluctuate based on factors like exam difficulty, candidate distributions, and total applicant choice preferences. If paper difficulty is high, the min score required for admission can drop.",
    category: "predictor"
  }
];

export default function FaqPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<"all" | "about" | "eligibility" | "predictor">("all");
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const filteredFaqs = FAQS.filter((faq, index) => {
    const matchesSearch =
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const categories = [
    { id: "all", name: "All Questions", icon: HelpCircle },
    { id: "about", name: "About CCMT", icon: BookOpen },
    { id: "eligibility", name: "Eligibility & Rounds", icon: GraduationCap },
    { id: "predictor", name: "Predictor Info", icon: BarChart3 }
  ] as const;

  return (
    <div className="min-h-screen bg-background selection:bg-primary/30 pt-10 pb-20">

      {/* Hero Section */}
      <section className="relative pt-12 pb-16 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full -z-10">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary/10 blur-[120px] rounded-full opacity-70" />
          <div className="absolute bottom-[10%] right-[-10%] w-[50%] h-[50%] bg-secondary/10 blur-[120px] rounded-full opacity-70" />
        </div>

        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface-container/50 border border-outline-variant/10 text-primary text-sm font-bold mb-6 shadow-sm backdrop-blur-md"
          >
            <HelpCircle className="size-4" />
            <span>Help Center</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-3xl md:text-5xl font-display font-black tracking-tight text-foreground mb-6 leading-tight"
          >
            Frequently Asked Questions
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-base md:text-lg text-on-surface-variant max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Have questions about CCMT counseling, cutoff predictions, or eligibility requirements? Find quick answers below.
          </motion.p>

          {/* Search Box */}
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="max-w-xl mx-auto relative mb-8"
          >
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant size-5" />
            <input
              type="text"
              placeholder="Search FAQ questions or keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex h-14 w-full rounded-2xl border border-outline-variant/20 bg-surface-container/70 px-4 pl-12 text-base text-foreground placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/50 backdrop-blur-md transition-all shadow-sm"
            />
          </motion.div>

          {/* Category Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex flex-wrap items-center justify-center gap-2 mb-12"
          >
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setExpandedIndex(null);
                  }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-bold transition-all ${
                    isActive
                      ? "bg-primary text-primary-foreground border-primary shadow-sm hover:opacity-90"
                      : "bg-surface-container/50 border-outline-variant/10 text-on-surface-variant hover:bg-surface-container hover:text-foreground"
                  }`}
                >
                  <Icon className="size-4" />
                  {cat.name}
                </button>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Accordion Questions List */}
      <section className="container mx-auto px-6 max-w-4xl">
        <div className="space-y-4">
          <AnimatePresence mode="popLayout">
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => {
                // Find global index in absolute FAQS array
                const globalIndex = FAQS.indexOf(faq);
                const isExpanded = expandedIndex === globalIndex;
                return (
                  <motion.div
                    key={faq.question}
                    layout
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -15 }}
                    transition={{ duration: 0.2 }}
                    className="group rounded-2xl bg-surface-container border border-outline-variant/20 overflow-hidden shadow-sm hover:border-primary/20 transition-all duration-300"
                  >
                    <button
                      onClick={() => toggleExpand(globalIndex)}
                      className="w-full text-left p-6 flex items-center justify-between gap-4 font-bold text-foreground text-base md:text-lg group-hover:text-primary transition-colors"
                    >
                      <span>{faq.question}</span>
                      <ChevronDown
                        className={`size-5 text-on-surface-variant transition-transform duration-300 flex-shrink-0 ${
                          isExpanded ? "rotate-180 text-primary" : ""
                        }`}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                          <div className="px-6 pb-6 pt-1 text-on-surface-variant leading-relaxed text-sm md:text-base border-t border-outline-variant/10 bg-surface-container-low/40">
                            {faq.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16 p-8 rounded-3xl bg-surface-container/50 border border-outline-variant/10 text-on-surface-variant"
              >
                <HelpCircle className="size-12 text-primary/30 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-foreground mb-2">No matching questions found</h3>
                <p className="text-sm max-w-md mx-auto">
                  We couldn&apos;t find any FAQs matching &quot;{searchTerm}&quot;. Try adjusting your search term or select another category filter.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* CTA Box */}
      <section className="container mx-auto px-6 max-w-4xl mt-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="p-8 md:p-10 rounded-3xl bg-surface-container border border-outline-variant/20 text-center relative overflow-hidden shadow-sm"
        >
          <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none" />
          <div className="relative z-10 space-y-6">
            <h3 className="text-xl md:text-2xl font-display font-black text-foreground">
              Still have doubts about your GATE admission options?
            </h3>
            <p className="text-on-surface-variant max-w-xl mx-auto text-sm md:text-base">
              Try our advanced prediction tools to estimate admission probability, compare rankings, and filter cutoffs based on your paper codes.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a href="/predictor">
                <Button className="h-12 px-6 rounded-xl font-bold gap-2">
                  <BarChart3 className="size-4" />
                  Try Predictor
                </Button>
              </a>
              <a href="/explorer">
                <Button variant="secondary" className="h-12 px-6 rounded-xl font-bold">
                  Explore Cutoffs
                </Button>
              </a>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
